import * as React from 'react';
import { type LucideIcon, Inbox } from 'lucide-react';

import { cn } from '../lib/utils';

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Icon to display (defaults to Inbox) */
  icon?: LucideIcon;
  /** Short title */
  title: string;
  /** Optional supporting description */
  description?: string;
  /** Optional action element (usually a Button) */
  action?: React.ReactNode;
  /**
   * Compact mode — icon on the left, title above description on the right.
   * Use inside cards / panels where vertical space is precious. The default
   * (false) keeps the classic centered stack used for full-page empty states.
   */
  compact?: boolean;
}

/**
 * Empty state placeholder shown when a list or view has no content.
 *
 * Default layout (centered, full-page friendly):
 *   ┌──────────────────────┐
 *   │     [icon]           │
 *   │    TITLE             │
 *   │    description       │
 *   │    [action]          │
 *   └──────────────────────┘
 *
 * Compact layout (`compact={true}`, embedded-friendly):
 *   ┌────────────────────────────────────┐
 *   │ [icon]  TITLE             [action] │
 *   │ [icon]  description                │
 *   └────────────────────────────────────┘
 *
 * @example
 * ```tsx
 * <EmptyState
 *   icon={Inbox}
 *   title="No work orders"
 *   description="Create your first work order to get started"
 *   action={<Button>New Work Order</Button>}
 * />
 * ```
 */
export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ icon: Icon = Inbox, title, description, action, compact = false, className, ...props }, ref) => {
    if (compact) {
      return (
        <div
          ref={ref}
          className={cn(
            'flex items-start gap-3 rounded-lg border border-dashed bg-card p-4',
            className
          )}
          {...props}
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
            <Icon className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-sm font-semibold text-foreground truncate">{title}</h3>
              {action && <div className="shrink-0">{action}</div>}
            </div>
            {description && (
              <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{description}</p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center rounded-lg border border-dashed bg-card px-6 py-8 text-center',
          className
        )}
        {...props}
      >
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {description && (
          <p className="mt-1 max-w-sm text-xs text-muted-foreground">
            {description}
          </p>
        )}
        {action && <div className="mt-3">{action}</div>}
      </div>
    );
  }
);

EmptyState.displayName = 'EmptyState';
